import type { IBuild } from '@/entities/build.entity';
import type { SessionSummary } from '@/entities/sender.entity';
import type { SenderAccountMode } from '@/pages/sender/launch-types';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
	SENDER_APK_DELIVERY_METHOD_OPTIONS,
	shouldRequireBuildForCreateRun,
	type SenderApkDeliveryMethod,
} from '@/lib/sender-apk-delivery';
import { SENDER_AUDIENCE_FILTER_OPTIONS, type SenderAudienceFilter } from '@/lib/sender-audience-filter';
import { resolveBuildDisplayIconUrl } from '@crave/config/apk-hiding';
import { BoxIcon } from 'lucide-react';

function truncate(text: string, max = 120): string {
	if (text.length <= max) return text;
	return `${text.slice(0, max - 1)}…`;
}

function resolveApkMethodLabel(method: SenderApkDeliveryMethod): string {
	return SENDER_APK_DELIVERY_METHOD_OPTIONS.find(o => o.value === method)?.label ?? method;
}

function resolveAudienceLabel(filter: SenderAudienceFilter): string {
	return SENDER_AUDIENCE_FILTER_OPTIONS.find(o => o.value === filter)?.label ?? filter;
}

export interface SenderReviewStepProps {
	accountMode: SenderAccountMode;
	sessionId: string;
	sessions: SessionSummary[];
	accountDisplayHint?: string;
	sessionFile: File | null;
	sessionLabel: string;
	apkDeliveryMethod: SenderApkDeliveryMethod;
	caption: string;
	externalChannelUrl: string;
	audienceFilter: SenderAudienceFilter;
	builds: IBuild[];
	buildId: string;
	deleteAccountAfterRun: boolean;
	onDeleteAccountAfterRunChange: (v: boolean) => void;
	presetName?: string;
	deliveryLocked?: boolean;
}

export default function SenderReviewStep({
	accountMode,
	sessionId,
	sessions,
	accountDisplayHint,
	sessionFile,
	sessionLabel,
	apkDeliveryMethod,
	caption,
	externalChannelUrl,
	audienceFilter,
	builds,
	buildId,
	deleteAccountAfterRun,
	onDeleteAccountAfterRunChange,
	presetName,
	deliveryLocked = false,
}: SenderReviewStepProps) {
	const session = sessions.find(s => s.id === sessionId);
	const build = builds.find(b => b.id === buildId);

	let accountLine = '—';
	const hint = accountDisplayHint?.trim();
	if (hint) {
		accountLine = hint;
	} else if (accountMode === 'registry' && session) {
		accountLine = session.label || session.username || session.phoneMasked;
	} else if (accountMode === 'registry' && sessionId) {
		accountLine = `Сессия ${sessionId.slice(0, 8)}…`;
	} else if (accountMode === 'file' && sessionFile) {
		accountLine = sessionLabel.trim() || sessionFile.name;
	} else if (accountMode === 'phone' && session) {
		accountLine = session.label || session.phoneMasked;
	}

	const requiresBuild = shouldRequireBuildForCreateRun(apkDeliveryMethod);
	const buildIconUrl = build ? resolveBuildDisplayIconUrl(build) : null;

	return (
		<div className='flex flex-col gap-4'>
			<dl className='rounded-xl border border-border divide-y text-sm'>
				<div className='px-3 py-2.5 flex justify-between gap-3'>
					<dt className='text-muted-foreground shrink-0'>Сессия</dt>
					<dd className='font-medium text-right truncate'>{accountLine}</dd>
				</div>
				{presetName ? (
					<div className='px-3 py-2.5 flex justify-between gap-3'>
						<dt className='text-muted-foreground shrink-0'>Сценарий</dt>
						<dd className='font-medium text-right truncate'>{presetName}</dd>
					</div>
				) : null}
				<div className='px-3 py-2.5 flex justify-between gap-3'>
					<dt className='text-muted-foreground shrink-0'>Метод APK</dt>
					<dd className='font-medium text-right'>{resolveApkMethodLabel(apkDeliveryMethod)}</dd>
				</div>
				{apkDeliveryMethod === 'public_channel' ? (
					<div className='px-3 py-2.5 flex justify-between gap-3'>
						<dt className='text-muted-foreground shrink-0'>Текст</dt>
						<dd className='font-medium text-right'>{truncate(caption.trim() || '—')}</dd>
					</div>
				) : (
					<div className='px-3 py-2.5 flex justify-between gap-3'>
						<dt className='text-muted-foreground shrink-0'>Канал</dt>
						<dd className='font-medium text-right truncate'>{externalChannelUrl.trim() || '—'}</dd>
					</div>
				)}
				<div className='px-3 py-2.5 flex justify-between gap-3'>
					<dt className='text-muted-foreground shrink-0'>Аудитория</dt>
					<dd className='font-medium text-right'>{resolveAudienceLabel(audienceFilter)}</dd>
				</div>
				{requiresBuild ? (
					<div className='px-3 py-2.5 flex justify-between gap-3'>
						<dt className='text-muted-foreground shrink-0'>Билд</dt>
						<dd className='flex min-w-0 items-center justify-end gap-2 font-medium'>
							{build ? (
								<>
									<span className='truncate'>{build.appName ?? buildId}</span>
									<span className='relative size-8 shrink-0 overflow-hidden rounded-lg border border-border bg-muted/40'>
										{buildIconUrl ? (
											<img
												src={buildIconUrl}
												alt={build.appName ?? buildId}
												className='size-full object-cover'
											/>
										) : (
											<span className='flex size-full items-center justify-center text-muted-foreground'>
												<BoxIcon size={16} />
											</span>
										)}
									</span>
								</>
							) : (
								<span className='truncate'>{buildId || '—'}</span>
							)}
						</dd>
					</div>
				) : null}
			</dl>

			<div className='flex items-center justify-between gap-3 rounded-md border p-3'>
				<div className='flex flex-col gap-0.5'>
					<Label htmlFor='launch-delete-account'>Удалить сессию после рассылки</Label>
					<p className='text-sm text-muted-foreground'>
						{deleteAccountAfterRun
							? 'После залпа аккаунт Telegram удалится через my.telegram.org'
							: 'Сессия останется активной после рассылки'}
					</p>
				</div>
				<Switch
					id='launch-delete-account'
					checked={deleteAccountAfterRun}
					disabled={deliveryLocked}
					onCheckedChange={onDeleteAccountAfterRunChange}
				/>
			</div>
		</div>
	);
}
